import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import axios from "../api/axios";

import './styles/LoginSignup.css';
import SEO from "../seo/SEO";
import config from "../config/config";

import { useAnalytics } from "../context/AnalyticsProvider";


const Signup = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [passwordsMatchError, setPasswordsMatchError] = useState(""); // ✅ Live error state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const analytics = useAnalytics();



  useEffect(() => {
    if (auth.isAuthenticated) {
      if (["admin", "siteadmin"].includes(auth.user?.role)) {
        navigate("/admin/dashboard");
      } else {
        navigate("/user");
      }
    }
  }, [auth, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  
    // ✅ Live character match check
    if (name === "confirmPassword") {
      const password = formData.password;
  
      if (
        password &&
        (value.length > password.length ||
          !password.startsWith(value))
      ) {
        setPasswordsMatchError("Passwords do not match.");
      } else {
        setPasswordsMatchError("");
      }
    }
  
    // ✅ Optional: clear error if password changes and confirmPassword is shorter
    if (name === "password" && formData.confirmPassword) {
      const confirmPassword = formData.confirmPassword;
  
      if (
        confirmPassword.length > value.length ||
        !value.startsWith(confirmPassword)
      ) {
        setPasswordsMatchError("Passwords do not match.");
      } else {
        setPasswordsMatchError("");
      }
    }
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      const { name, email, password } = formData;
      const response = await axios.post(
        "/api/auth/register",
        { name, email, password },
        { withCredentials: true }
      );

      if (!response.data.user) {
        throw new Error(response.data.error || "Signup failed");
      }

      analytics.track("sign_up", {
        method: "email_password",
      });

      setSuccess(true);
      navigate("/verify-email", { state: { email: email } });
    } catch (err) {
      setError(err.response?.data?.error || "Signup failed.");
    }
  };

  return (
    <>


          <SEO
              robots="noindex, follow"
              title="Sign Up - Withrow Knives"
              description="Create your Withrow Knives account to shop custom handmade knives, track orders, and access exclusive offers."
              keywords={[
                  "Withrow Knives signup",
                  "create account",
                  "register",
                  "handmade knives",
                  "custom knives"
              ]}
              url={`${config.metadata.siteUrl}/signup`}
              jsonLd={{
                  "@context": "https://schema.org",
                  "@type": "WebPage",
                  "name": "Sign Up - Withrow Knives",
                  "url": `${config.metadata.siteUrl}/signup`,
                  "description": "Create your Withrow Knives account to shop custom handmade knives, track orders, and access exclusive offers.",
                  "breadcrumb": {
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                          {
                              "@type": "ListItem",
                              "position": 1,
                              "name": "Home",
                              "item": config.metadata.siteUrl
                          },
                          {
                              "@type": "ListItem",
                              "position": 2,
                              "name": "Sign Up",
                              "item": `${config.metadata.siteUrl}/signup`
                          }
                      ]
                  },
                  "mainEntityOfPage": {
                      "@type": "WebPage",
                      "@id": `${config.metadata.siteUrl}/signup`
                  }
              }}
          />




    
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <h2>Sign Up</h2>
          <p>
            Already have an account? <Link to="/login">Login</Link>
          </p>
          {error && <p className="error">{error}</p>}
          {success && <p className="success">Signup successful! Redirecting...</p>}

          <form onSubmit={handleSubmit}>
            {/* Full Name Field */}
            <label htmlFor="signup-name" className="sr-only">Full Name</label>
            <input
              id="signup-name"
              type="text"
              name="name"
              placeholder="Full Name"
              onChange={handleChange}
              required
            />

            {/* Email Field */}
            <label htmlFor="signup-email" className="sr-only">Email</label>
            <input
              id="signup-email"
              type="email"
              name="email"
              placeholder="Email"
              onChange={handleChange}
              required
            />

            {/* Password Field */}
            <div className="password-wrapper">
              <label htmlFor="signup-password" className="sr-only">Password</label>
              <input
                id="signup-password"
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Password"
                onChange={handleChange}
                required
                aria-describedby="password-description"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
            <p id="password-description" className="sr-only">
              Password must be at least 8 characters long and include a mix of letters and numbers.
            </p>

            {/* Confirm Password Field */}
            <div className="password-wrapper">
              <label htmlFor="signup-confirm-password" className="sr-only">Confirm Password</label>
              <input
                id="signup-confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm Password"
                onChange={handleChange}
                required
                aria-describedby="confirm-password-description"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                <i className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
            <p id="confirm-password-description" className="sr-only">
              Re-enter your password to confirm it matches the first entry.
            </p>

            {/* ✅ Live password mismatch error */}
            {passwordsMatchError && <p className="error" aria-live="polite">{passwordsMatchError}</p>}

            <button type="submit">Sign Up</button>
          </form>
        </div>
      </div>

    </>
  );
};

export default Signup;
